import React, { useState, useEffect } from "react";
import axios from "axios";
import { getBackendUrl } from '../../utils/env';
import { Card, Button, Input, Form, App, Typography, Space, Breadcrumb, theme, Row, Col, List, Badge, Tag } from "antd";
import {
  SendOutlined, HistoryOutlined, LinkOutlined,
  NotificationOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;

export default function AdminNotificationsDashboard() {
  const { message: antdMessage } = App.useApp();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  // 🔹 Obtener historial de notificaciones
  useEffect(() => {
    axios
      .get(`${getBackendUrl()}api/admin/notifications.php?action=list`)
      .then((res) => {
        const data = res.data;
        if (data.success && Array.isArray(data.notifications)) {
          setNotifications(data.notifications);
        } else {
          setNotifications([]);
          antdMessage.warning(data.message || "Respuesta inesperada del servidor");
        }
      })
      .catch(() => antdMessage.error("No se pudo cargar el historial de notificaciones"));
  }, []);

  // 🔹 Enviar notificación nueva
  const handleSend = async (values) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${getBackendUrl()}api/admin/notifications.php?action=create`,
        values
      );

      if (data.success) {
        setNotifications([data.notification, ...notifications]);
        form.resetFields();
        antdMessage.success("Notificación enviada correctamente");
      } else {
        antdMessage.error(data.message || "Error al enviar notificación");
        console.error("Error details:", data);
      }
    } catch (err) {
      antdMessage.error("Error en la conexión con el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: 'clamp(16px, 4vw, 32px)',
      maxWidth: 1200,
      margin: "0 auto",
      backgroundColor: token.colorBgLayout,
      minHeight: '100vh'
    }}>
      {/* NAVEGACIÓN */}
      <Breadcrumb
        style={{ marginBottom: 24 }}
        items={[
          { title: <span onClick={() => navigate("/admin")} style={{ cursor: "pointer", color: token.colorPrimary }}>Panel de Administración</span> },
          { title: "Gestión de Notificaciones" },
        ]}
      />

      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ margin: 0 }}>
          <NotificationOutlined style={{ marginRight: 12, color: token.colorPrimary }} />
          Notificaciones Push
        </Title>
        <Text type="secondary">Envía avisos directos a los dispositivos de la comunidad mediante OneSignal.</Text>
      </div>

      <Row gutter={[32, 32]}>
        {/* --- COLUMNA IZQUIERDA: FORMULARIO --- */}
        <Col xs={24} lg={10}>
          <Card
            title={<Space><SendOutlined /> Nueva Notificación</Space>}
            style={{
              borderRadius: 20,
              boxShadow: token.boxShadowSecondary,
              position: 'sticky',
              top: 88
            }}
          >
            <Form form={form} layout="vertical" onFinish={handleSend}>
              <Form.Item
                label="Título"
                name="title"
                rules={[{ required: true, message: "El título es obligatorio" }]}
              >
                <Input placeholder="Ej: ¡Nuevo premio en la tienda!" allowClear size="large" />
              </Form.Item>

              <Form.Item
                label="Mensaje"
                name="message"
                rules={[{ required: true, message: "El mensaje es obligatorio" }]}
              >
                <Input.TextArea rows={4} placeholder="Describe el anuncio de forma breve y atractiva..." allowClear />
              </Form.Item>

              <Form.Item label="Enlace de destino (URL)" name="url">
                <Input
                  prefix={<LinkOutlined style={{ color: token.colorTextDisabled }} />}
                  placeholder="https://app.com/tienda"
                  size="large"
                  allowClear
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SendOutlined />}
                size="large"
                block
                style={{ borderRadius: 12, height: 48, fontWeight: 700 }}
              >
                Enviar a todos los dispositivos
              </Button>
            </Form>
          </Card>
        </Col>

        {/* --- COLUMNA DERECHA: HISTORIAL --- */}
        <Col xs={24} lg={14}>
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={4} style={{ margin: 0 }}>
              <HistoryOutlined style={{ marginRight: 8 }} /> Historial Reciente
            </Title>
            <Badge count={notifications.length} color={token.colorPrimary} />
          </div>

          <Card style={{ borderRadius: 20, boxShadow: token.boxShadowTertiary }}>
            <List
              itemLayout="vertical"
              dataSource={notifications}
              locale={{ emptyText: <Text type="secondary">No se han enviado notificaciones aún.</Text> }}
              renderItem={(n) => (
                <List.Item
                  key={n.id}
                  style={{
                    padding: '20px',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    transition: 'background 0.3s'
                  }}
                  className="notification-item"
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Title level={5} style={{ margin: 0 }}>{n.title}</Title>
                    <Tag icon={<ClockCircleOutlined />} color="default">
                      {new Date(n.created_at).toLocaleDateString()}
                    </Tag>
                  </div>

                  <Paragraph style={{ marginTop: 8, color: token.colorTextSecondary }}>
                    {n.message}
                  </Paragraph>

                  {n.url && n.url.startsWith('http') && (
                    <Tag
                      icon={<LinkOutlined />}
                      color="blue"
                      style={{ cursor: 'pointer', borderRadius: 6, marginTop: 8 }}
                      onClick={() => window.open(n.url, '_blank')}
                    >
                      {/* Intentamos parsear, si falla mostramos "Ver enlace" */}
                      {(() => {
                        try {
                          return new URL(n.url).hostname;
                        } catch (e) {
                          return "Ver enlace";
                        }
                      })()}
                    </Tag>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Enviado a las {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <style>{`
        .notification-item:hover {
          background-color: ${token.colorFillAlter};
        }
      `}</style>
    </div>
  );
}